import { bind } from '../../../helpers/decorators';
import { createLogger } from '../../../helpers/loggerFacade';
import { RoomAgentError } from '../errors/RoomAgentError';
import { isH264ProfileLevelIdSupported } from '../helpers';
import { SDKAdapter, SdpMSection } from '../types';
import { RecorderAudio } from './RecorderAudio';
import { RecorderVideo, Resolution } from './RecorderVideo';

const logger = createLogger('room-agent-recorder-manager');

export const RECORDER_RESOLUTIONS: Record<string | number, Resolution> = {
    720: { width: 1280, height: 720 },
    1080: { width: 1920, height: 1080 },
};

interface ConstructorParams {
    adapter: SDKAdapter;
    defaultH264ProfileLevelId?: string;
    sections?: SdpMSection[];
}

export class RecorderManager {
    public recorders: Array<RecorderVideo | RecorderAudio> = [];
    private adapter: SDKAdapter;
    /**
     * https://en.wikipedia.org/wiki/Advanced_Video_Coding#Profiles
     * По умолчанию выбирается низкий level-id
     * Пока идеальный 42001f (aka Base line, флаги по 0, level 3.1 - 720p)
     */
    private defaultH264ProfileLevelId?: string;
    private sections?: SdpMSection[];

    constructor({ adapter, defaultH264ProfileLevelId, sections }: ConstructorParams) {
        this.adapter = adapter;
        this.defaultH264ProfileLevelId = defaultH264ProfileLevelId;
        this.sections = sections;

        this.createRecorders();
    }

    private createDefaultSections(): SdpMSection[] {
        return [{ type: 'audio' }, { type: 'video', h264profile: this.defaultH264ProfileLevelId }];
    }

    private getSections(): SdpMSection[] {
        const sections = this.sections || this.createDefaultSections();

        return sections.map((section) => {
            if (section.type === 'video') {
                const isProvidedProfileIdSupported =
                    section.h264profile && isH264ProfileLevelIdSupported(section.h264profile);

                if (!isProvidedProfileIdSupported) {
                    logger.warn('provided h264 profile is not supported', {
                        method: 'getSections',
                        additional: {
                            profile: section.h264profile,
                        },
                    });
                }

                return {
                    ...section,
                    h264profile: isProvidedProfileIdSupported ? section.h264profile : this.defaultH264ProfileLevelId,
                };
            }

            return section;
        });
    }

    private createRecorders(): void {
        const sections = this.getSections();
        const audioRecorders = sections
            .filter(({ type }) => type === 'audio')
            .map(
                ({ content }, idx) =>
                    new RecorderAudio({
                        tracks: idx === 0 ? this.adapter.getRemoteTracks() : [],
                        content,
                    })
            );
        const videoRecorders = sections
            .filter(({ type }) => type === 'video')
            .map(({ h264profile, content }) => {
                return new RecorderVideo({
                    resolution: RECORDER_RESOLUTIONS[1080],
                    h264ProfileLevelId: h264profile,
                    content,
                });
            });

        this.recorders = [...audioRecorders, ...videoRecorders];
    }

    @bind
    public getVideoRecorders(): RecorderVideo[] {
        return this.recorders.filter((recorder) => recorder instanceof RecorderVideo) as RecorderVideo[];
    }

    @bind
    public getAudioRecorders(): RecorderAudio[] {
        return this.recorders.filter((recorder) => recorder instanceof RecorderAudio) as RecorderAudio[];
    }

    @bind
    public getTrackContentDescriptors(): Array<{ content?: string; trackId: string }> {
        return this.recorders.map(({ content, getTrack }) => ({ content, trackId: getTrack().id }));
    }

    @bind
    public getVideoTrackH264ProfileLevelIdDescriptors(): Array<{ h264profile?: string; trackId: string }> {
        return this.getVideoRecorders().map(({ h264ProfileLevelId, getTrack }) => ({
            h264ProfileLevelId,
            trackId: getTrack().id,
        }));
    }

    @bind
    public getMainVideoRecorder(): RecorderVideo {
        const videoRecorders = this.getVideoRecorders();

        const recorder = videoRecorders.find((recorder) => recorder.content === 'main') || videoRecorders[0];

        if (!recorder) {
            throw new RoomAgentError('Recorder error', "Can't get main video recorder", {
                method: 'RecorderManager:getMainVideoRecorder',
            });
        }

        return recorder;
    }

    @bind
    public getMainAudioRecorder(): RecorderAudio {
        const audioRecorders = this.getAudioRecorders();
        const recorder = audioRecorders.find((recorder) => recorder.content === 'main') || audioRecorders[0];

        if (!recorder) {
            throw new RoomAgentError('Recorder error', "Can't get main audio recorder", {
                method: 'RecorderManager:getMainAudioRecorder',
            });
        }

        return recorder;
    }
}
